import { toast } from '@crono-one/design-system/side-effects';
import { ContactMethod } from 'components/ContactInfo';
import { FormInputRow } from 'components/CreationForms/base/FormInput';
import { useAiFieldsToEnrich } from 'components/CreationForms/fields/AiFieldsEnrichPicker/useAiFieldsToEnrich';
import { useCompanyNameCheck } from 'components/CreationForms/fields/CompanyNameField/useCompanyNameCheck';
import { ContactMethodRow } from 'components/CreationForms/fields/ContactMethodField';
import { useSyncTypeSelection } from 'components/CreationForms/fields/SyncTypePicker/useSyncTypeSelection';
import { useCreationExternalProperties } from 'components/CreationForms/hooks/useCreationExternalProperties';
import { useNativeMandatoryFields } from 'components/CreationForms/hooks/useNativeMandatoryFields';
import { useListSelection } from 'components/CreationForms/sections/ListSection/useListSelection';
import {
  getFieldsError,
  isValidContactUrl,
  toNumberOrNull,
  trimmedOrNull,
} from 'components/CreationForms/utils/fields';
import { useAnalytics } from 'context/analytics';
import { useAuth } from 'context/auth';
import { Account } from 'crono-fe-common/types/account';
import { LinkedinCompanyCompleteInfo } from 'crono-fe-common/types/crono-extension/linkedin';
import { AccountInsert } from 'crono-fe-common/types/DTO/accountInsert';
import { ListType } from 'crono-fe-common/types/list';
import { withMeta } from 'crono-fe-common/types/mutationMeta';
import useCreateAccount from 'hooks/services/account/useCreateAccount';
import usePatchProspectAccount from 'hooks/services/prospect/usePatchProspectAccount';
import useGetUserCredits from 'hooks/services/user/useGetUserCredits';
import { useState } from 'react';

/** The "Company info" fields after the name, in their order. */
export const ACCOUNT_INFO_FIELDS = [
  {
    field: 'country',
    label: 'Country',
    placeholder: 'Enter a text',
    type: 'text',
  },
  {
    field: 'industry',
    label: 'Industry',
    placeholder: 'Enter a text',
    type: 'text',
  },
  {
    field: 'numberOfEmployees',
    label: 'Employees',
    placeholder: 'Enter a numeric value',
    type: 'number',
  },
  {
    field: 'annualRevenue',
    label: 'Revenue',
    placeholder: 'Enter a text',
    type: 'text',
  },
  {
    field: 'currentSolution',
    label: 'Solution',
    placeholder: 'Enter a text',
    type: 'text',
  },
] as const satisfies readonly FormInputRow<keyof AccountInsert>[];

type AccountInfoField = (typeof ACCOUNT_INFO_FIELDS)[number]['field'];

type AccountContactMethod =
  | ContactMethod.Phone
  | ContactMethod.Linkedin
  | ContactMethod.Website;

/** The "Company details" rows, in their order. */
export const ACCOUNT_CONTACT_METHOD_ROWS = [
  {
    method: ContactMethod.Phone,
    label: 'Phone',
    placeholder: 'Insert phone number',
  },
  {
    method: ContactMethod.Linkedin,
    label: 'LinkedIn',
    placeholder: 'Insert LinkedIn URL',
  },
  {
    method: ContactMethod.Website,
    label: 'Website',
    placeholder: 'Insert website URL',
  },
] as const satisfies readonly ContactMethodRow<AccountContactMethod>[];

export type CompanyTextField = 'name' | AccountInfoField | AccountContactMethod;

export type CompanyValues = Record<CompanyTextField, string>;

const EMPTY_COMPANY: CompanyValues = {
  name: '',
  country: '',
  industry: '',
  numberOfEmployees: '',
  annualRevenue: '',
  currentSolution: '',
  phone: '',
  linkedin: '',
  website: '',
};

export const NAME_LABEL = 'Company name';

/** Label of every field the form shows, by its payload key, for the mandatory check. */
const FIELD_LABELS: Record<string, string> = {
  name: NAME_LABEL,
  ...Object.fromEntries(
    ACCOUNT_INFO_FIELDS.map(({ field, label }) => [field, label]),
  ),
  ...Object.fromEntries(
    ACCOUNT_CONTACT_METHOD_ROWS.map(({ method, label }) => [method, label]),
  ),
};

/** What an entry point knows about the company beyond the form's fields, e.g. its LinkedIn numeric id. */
export type CompanyEntryPointPayload = Partial<
  Pick<AccountInsert, 'linkedinNumericId'>
>;

/** The picked LinkedIn company's fields, empty where LinkedIn doesn't know them. */
const getLinkedinCompanyFields = (
  info: LinkedinCompanyCompleteInfo,
): CompanyValues => ({
  ...EMPTY_COMPANY,
  ...(info.name && { name: info.name }),
  ...(info.country && { country: info.country }),
  ...(info.industry && { industry: info.industry }),
  ...(info.numberOfEmployees !== null && {
    numberOfEmployees: String(info.numberOfEmployees),
  }),
  ...(info.phone && { phone: info.phone }),
  ...(info.url && { linkedin: info.url }),
  ...(info.website && { website: info.website }),
});

interface UseCompanyFormParams {
  /** What the company fields start from; a field follows it until it's edited. */
  initialCompany?: Partial<CompanyValues>;
  /** The contact the new company is assigned to, instead of only creating it. */
  prospectId?: string;
  /** Called with the created company, before the form closes. */
  onCreated?: (account: Account) => void;
  /** Called with the new company once it's assigned to the contact. */
  onAssigned?: (accountId: string | undefined) => void;
  onClose: () => void;
}

/**
 * State, mandatory check and creation of a single company: it's created on its
 * own, or created and assigned to a contact.
 */
export const useCompanyForm = ({
  initialCompany,
  prospectId,
  onCreated,
  onAssigned,
  onClose,
}: UseCompanyFormParams) => {
  const analytics = useAnalytics();
  const { user } = useAuth();
  const { data: creditsData } = useGetUserCredits();
  const credits = creditsData?.data?.data;

  // account data
  const startingCompany: CompanyValues = {
    ...EMPTY_COMPANY,
    ...initialCompany,
  };
  const [companyEdits, setCompanyEdits] = useState<Partial<CompanyValues>>({});
  const company: CompanyValues = { ...startingCompany, ...companyEdits };
  const [linkedinNumericId, setLinkedinNumericId] = useState<string | null>(
    null,
  );
  const nativeFields = useNativeMandatoryFields({ entity: 'account' });
  const name = company.name.trim();
  const nameCheck = useCompanyNameCheck(company.name);

  // creation options
  const syncType = useSyncTypeSelection();

  // owner and list selections
  const [ownerId, setOwnerId] = useState(user?.id);
  const listSelection = useListSelection(ListType.Account);

  // tags, external properties and AI variables
  const customFields = useCreationExternalProperties('Account');
  const aiFields = useAiFieldsToEnrich('Account', credits?.aiLeft);

  const { mutate: createAccount, isPending: isCreatingAccount } =
    useCreateAccount();
  const { mutate: assignAccount, isPending: isAssigningAccount } =
    usePatchProspectAccount();

  const isDirty =
    Object.entries(company).some(
      ([field, value]) =>
        value.trim() !== startingCompany[field as CompanyTextField].trim(),
    ) ||
    syncType.isDirty ||
    ownerId !== user?.id ||
    listSelection.isDirty ||
    customFields.isDirty ||
    aiFields.isDirty;
  const isSubmitting = isCreatingAccount || isAssigningAccount;

  const setCompanyField = (field: CompanyTextField, value: string) => {
    setCompanyEdits((prev) => ({ ...prev, [field]: value }));
    // The id belongs to the LinkedIn company that was picked, not to an edited URL.
    if (field === ContactMethod.Linkedin) setLinkedinNumericId(null);
  };

  const handleLinkedinCompanyPick = (info: LinkedinCompanyCompleteInfo) => {
    // The form becomes the picked company: nothing typed for another one is kept.
    setCompanyEdits(getLinkedinCompanyFields(info));
    setLinkedinNumericId(info.numericId);
  };

  const getMissingFields = () => {
    const missing = nativeFields.getMissingFields(company, FIELD_LABELS);
    if (!name && !missing.includes(NAME_LABEL)) missing.unshift(NAME_LABEL);
    return [...missing, ...customFields.getMissingMandatory()];
  };

  /** Labels of the LinkedIn and website fields with an invalid address. */
  const getInvalidFields = () =>
    ([ContactMethod.Linkedin, ContactMethod.Website] as const)
      .filter(
        (method) => !isValidContactUrl(method, 'company', company[method]),
      )
      .map((method) => FIELD_LABELS[method]);

  /** Creates the company with the entry point's own fields, e.g. its LinkedIn ids. */
  const handleSubmit = (entryPointPayload: CompanyEntryPointPayload = {}) => {
    const missing = getMissingFields();
    const invalid = getInvalidFields();
    if (missing.length > 0 || invalid.length > 0) {
      toast.error(getFieldsError({ missing, invalid }));
      return;
    }

    const account: AccountInsert = {
      // account data
      name,
      country: trimmedOrNull(company.country),
      industry: trimmedOrNull(company.industry),
      numberOfEmployees: toNumberOrNull(company.numberOfEmployees),
      annualRevenue: trimmedOrNull(company.annualRevenue),
      currentSolution: trimmedOrNull(company.currentSolution),
      phone: trimmedOrNull(company.phone),
      linkedin: trimmedOrNull(company.linkedin),
      linkedinNumericId,
      website: trimmedOrNull(company.website),

      // creation options
      createInCrm: syncType.createInCrm,

      // owner and list selections
      userId: ownerId,
      listId: listSelection.selectedList?.id ?? null,

      // tags, external properties and AI variables
      externalValues: customFields.toPayload(),
      ...(aiFields.selectedIds.length > 0 && {
        generateAiVariables: { externalPropertyIds: aiFields.selectedIds },
      }),

      ...entryPointPayload,
    };

    if (listSelection.selectedList) {
      analytics?.track('add-account-to-list', {
        listId: listSelection.selectedList.id,
      });
    }

    if (prospectId) {
      assignAccount(
        withMeta(
          { prospectId, accountInsert: account },
          {
            successMessage: `Contact linked to ${name} — strategy and tasks stopped`,
          },
        ),
        {
          onSuccess: (response) => {
            onAssigned?.(response.data?.data?.accountId);
            onClose();
          },
        },
      );
      return;
    }

    createAccount(
      withMeta(
        { data: account },
        { successMessage: 'Company created successfully' },
      ),
      {
        onSuccess: (response) => {
          const created = response.data?.data;
          if (created) onCreated?.(created);
          onClose();
        },
      },
    );
  };

  return {
    // account data
    company,
    name,
    nameCheck,
    nativeFields,

    // creation options
    syncType,

    // owner and list selections
    ownerId,
    listSelection,

    // tags, external properties and AI variables
    customFields,
    aiFields,
    credits,

    isDirty,
    isSubmitting,

    setCompanyField,
    setOwnerId,
    handleLinkedinCompanyPick,
    handleSubmit,
  };
};

export type CompanyForm = ReturnType<typeof useCompanyForm>;
